import React, { useState, useEffect } from 'react';
import {
  subscribeOfferVersions,
  submitIndicativeOffer,
  createFormalPdfVersion,
  appendOfferPdfArtifact,
  acceptListingOfferVersion,
} from '../../services/listingOfferService';
import { getShareInterestFileDownloadUrl } from '../../services/shareInterestService';

/**
 * Courtier vendeur : pistes d’offres pour un interest (mandat de vente).
 * Destinataire : saisie offre indicative puis suivi.
 */
export default function MandatedInterestOffersPanel({
  shareToken,
  interestId,
  viewerRole,
  ownerUid,
  recipientUid,
  t,
}) {
  const [versions, setVersions] = useState([]);
  const [price, setPrice] = useState('');
  const [conditions, setConditions] = useState('');
  const [delays, setDelays] = useState('');
  const [comments, setComments] = useState('');
  const [repBroker, setRepBroker] = useState(false);
  const [brokerEmail, setBrokerEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [formalFile, setFormalFile] = useState(null);
  const [artifactFile, setArtifactFile] = useState(null);
  const [artifactTargetId, setArtifactTargetId] = useState('');
  /** Masque le formulaire d’offre indicative sans supprimer les données (réaffichable). */
  const [indicativeFormHidden, setIndicativeFormHidden] = useState(false);

  useEffect(() => {
    if (!shareToken || !interestId) return undefined;
    return subscribeOfferVersions(shareToken, interestId, setVersions);
  }, [shareToken, interestId]);

  const indicative = versions.filter((v) => v.kind === 'indicative_buyer');
  const formals = versions.filter((v) => v.kind === 'formal_pdf');
  const hasIndicative = indicative.length > 0;

  const handleSubmitIndicative = async () => {
    setErr('');
    if (!recipientUid) return;
    if (repBroker && (!brokerEmail.trim() || !brokerEmail.includes('@'))) {
      setErr(t('listingOffer.brokerEmailInvalid'));
      return;
    }
    setBusy(true);
    try {
      await submitIndicativeOffer(shareToken, interestId, recipientUid, {
        price,
        conditions,
        delays,
        comments,
        buyerRepresentedByBroker: repBroker,
        buyerBrokerEmail: repBroker ? brokerEmail.trim() : null,
      });
      setPrice('');
      setConditions('');
      setDelays('');
      setComments('');
      setRepBroker(false);
      setBrokerEmail('');
    } catch (e) {
      setErr(e?.message || t('listingOffer.submitError'));
    } finally {
      setBusy(false);
    }
  };

  const handleCreateFormal = async () => {
    setErr('');
    if (!ownerUid || !formalFile) {
      setErr(t('listingOffer.pdfRequired'));
      return;
    }
    setBusy(true);
    try {
      const vid = await createFormalPdfVersion(shareToken, interestId, ownerUid, {});
      await appendOfferPdfArtifact(shareToken, interestId, vid, formalFile, ownerUid, 'selling_broker', 'formal_offer');
      setFormalFile(null);
    } catch (e) {
      setErr(e?.message || t('listingOffer.submitError'));
    } finally {
      setBusy(false);
    }
  };

  const handleAppendArtifact = async () => {
    setErr('');
    if (!artifactTargetId || !artifactFile) return;
    const uid = viewerRole === 'owner' ? ownerUid : recipientUid;
    const role = viewerRole === 'owner' ? 'selling_broker' : 'buyer';
    setBusy(true);
    try {
      await appendOfferPdfArtifact(
        shareToken,
        interestId,
        artifactTargetId,
        artifactFile,
        uid,
        role,
        role === 'buyer' ? 'signed_return' : 'attachment',
      );
      setArtifactFile(null);
      setArtifactTargetId('');
    } catch (e) {
      setErr(e?.message || t('listingOffer.submitError'));
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async (vid) => {
    if (!ownerUid) return;
    if (!window.confirm(t('listingOffer.acceptConfirm'))) return;
    setBusy(true);
    try {
      await acceptListingOfferVersion(shareToken, interestId, vid, ownerUid);
    } catch (e) {
      setErr(e?.message || t('listingOffer.submitError'));
    } finally {
      setBusy(false);
    }
  };

  if (viewerRole === 'recipient' && !hasIndicative) {
    if (indicativeFormHidden) {
      return (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <p className="mb-2">{t('listingOffer.indicativeFormHiddenHint')}</p>
          <button
            type="button"
            className="text-indigo-700 font-medium underline hover:no-underline"
            onClick={() => setIndicativeFormHidden(false)}
          >
            {t('listingOffer.showIndicativeFormAgain')}
          </button>
        </div>
      );
    }
    return (
      <div className="mt-3 space-y-3 border border-indigo-200 rounded-lg p-3 bg-white">
        <p className="text-sm font-medium text-indigo-900">{t('listingOffer.indicativeTitle')}</p>
        {err ? <p className="text-sm text-red-600">{err}</p> : null}
        <input
          className="w-full border rounded px-2 py-1.5 text-sm"
          placeholder={t('listingOffer.price')}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <textarea
          className="w-full border rounded px-2 py-1.5 text-sm"
          rows={2}
          placeholder={t('listingOffer.conditions')}
          value={conditions}
          onChange={(e) => setConditions(e.target.value)}
        />
        <textarea
          className="w-full border rounded px-2 py-1.5 text-sm"
          rows={2}
          placeholder={t('listingOffer.delays')}
          value={delays}
          onChange={(e) => setDelays(e.target.value)}
        />
        <textarea
          className="w-full border rounded px-2 py-1.5 text-sm"
          rows={2}
          placeholder={t('listingOffer.comments')}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={repBroker} onChange={(e) => setRepBroker(e.target.checked)} />
          {t('listingOffer.representedByBroker')}
        </label>
        {repBroker ? (
          <input
            type="email"
            className="w-full border rounded px-2 py-1.5 text-sm"
            placeholder={t('listingOffer.brokerEmail')}
            value={brokerEmail}
            onChange={(e) => setBrokerEmail(e.target.value)}
          />
        ) : null}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={handleSubmitIndicative}
            className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {t('listingOffer.submitIndicative')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setIndicativeFormHidden(true)}
            className="px-3 py-2 rounded-lg text-sm font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            {t('listingOffer.cancelIndicativeForm')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3 border border-slate-200 rounded-lg p-3 bg-white">
      <p className="text-sm font-medium text-slate-800">{t('listingOffer.versionsTitle')}</p>
      {err ? <p className="text-sm text-red-600">{err}</p> : null}
      <ul className="space-y-2 text-sm">
        {versions.map((v) => (
          <li key={v.id} className="border border-slate-100 rounded p-2">
            <div className="font-medium text-slate-900">
              #{v.versionNumber} — {v.kind === 'indicative_buyer' ? t('listingOffer.kindIndicative') : t('listingOffer.kindFormal')}
              {v.status ? ` — ${v.status}` : ''}
            </div>
            {v.kind === 'indicative_buyer' ? (
              <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                <div>{t('listingOffer.price')}: {v.price}</div>
                {v.buyerRepresentedByBroker ? (
                  <div>
                    {t('listingOffer.brokerEmail')}: {v.buyerBrokerEmail || '—'}
                  </div>
                ) : null}
              </div>
            ) : null}
            {(v.artifacts || []).map((a, i) => (
              <div key={i} className="text-xs mt-1">
                <button
                  type="button"
                  className="text-blue-600 hover:underline"
                  onClick={() =>
                    getShareInterestFileDownloadUrl(a.storagePath).then((url) => {
                      window.open(url, '_blank', 'noopener,noreferrer');
                    })
                  }
                >
                  {a.name} ({a.actorRole})
                </button>
                <span className="text-slate-500"> — {a.uploadedAt}</span>
              </div>
            ))}
            {viewerRole === 'owner' && v.kind === 'formal_pdf' && v.status !== 'accepted' && v.status !== 'superseded' ? (
              <button
                type="button"
                className="mt-2 text-xs px-2 py-1 bg-emerald-600 text-white rounded"
                disabled={busy}
                onClick={() => handleAccept(v.id)}
              >
                {t('listingOffer.acceptOffer')}
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      {viewerRole === 'owner' && (
        <div className="pt-2 border-t border-slate-100 space-y-2">
          <p className="text-xs font-medium text-slate-700">{t('listingOffer.addFormalPdf')}</p>
          <input type="file" accept=".pdf,application/pdf" onChange={(e) => setFormalFile(e.target.files?.[0] || null)} />
          <button
            type="button"
            disabled={busy || !formalFile}
            onClick={handleCreateFormal}
            className="text-sm px-3 py-1.5 bg-slate-800 text-white rounded disabled:opacity-50"
          >
            {t('listingOffer.uploadFormal')}
          </button>
        </div>
      )}

      {(viewerRole === 'recipient' || viewerRole === 'owner') && formals.length > 0 ? (
        <div className="pt-2 border-t border-slate-100 space-y-2">
          <p className="text-xs font-medium text-slate-700">{t('listingOffer.appendPdf')}</p>
          <select
            className="w-full text-sm border rounded px-2 py-1"
            value={artifactTargetId}
            onChange={(e) => setArtifactTargetId(e.target.value)}
          >
            <option value="">{t('listingOffer.chooseVersion')}</option>
            {formals.map((v) => (
              <option key={v.id} value={v.id}>
                #{v.versionNumber} ({v.status})
              </option>
            ))}
          </select>
          <input type="file" accept=".pdf,application/pdf" onChange={(e) => setArtifactFile(e.target.files?.[0] || null)} />
          <button
            type="button"
            disabled={busy || !artifactTargetId || !artifactFile}
            onClick={handleAppendArtifact}
            className="text-sm px-3 py-1.5 border border-slate-400 rounded disabled:opacity-50"
          >
            {t('listingOffer.uploadAttachment')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
